<template>
  <div class="p-0">
    <header class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Bandeja de solicitudes</h1>
        <p class="mt-1 text-sm text-gray-600">Gestiona las ausencias y licencias del personal academico.</p>
      </div>
      <div class="flex flex-wrap gap-2">
        <router-link to="/leave/calendar" class="secondary-button">
          <i class="fa-regular fa-calendar mr-2"></i>
          Calendario
        </router-link>
        <router-link to="/leave/requests/new?as=manager" class="primary-button">
          <i class="fa-solid fa-plus mr-2"></i>
          Nueva solicitud
        </router-link>
      </div>
    </header>

    <nav class="module-tabs">
      <router-link to="/leave/inbox" class="module-tab" :class="tabClass('/leave/inbox')">Bandeja de revision</router-link>
      <router-link to="/leave/requests" class="module-tab" :class="tabClass('/leave/requests')">Mis solicitudes</router-link>
      <router-link to="/leave/calendar" class="module-tab" :class="tabClass('/leave/calendar')">Calendario</router-link>
      <router-link to="/leave/admin/articles" class="module-tab" :class="tabClass('/leave/admin')">Administracion</router-link>
    </nav>

    <section class="filter-card">
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
        <div>
          <label class="label">Estado</label>
          <select v-model="filters.status" @change="fetchRequests" class="field">
            <option v-for="status in statuses" :key="status.value" :value="status.value">
              {{ status.label }}
            </option>
          </select>
        </div>
        <div>
          <label class="label">Docente</label>
          <select v-model="filters.user_id" @change="fetchRequests" class="field">
            <option value="">Todos los docentes</option>
            <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
              {{ teacherFullName(teacher) }}
            </option>
          </select>
        </div>
        <div>
          <label class="label">Articulo</label>
          <select v-model="filters.leave_article_id" @change="fetchRequests" class="field">
            <option value="">Todos</option>
            <option v-for="article in articles" :key="article.id" :value="article.id">
              {{ articleTitle(article) }}
            </option>
          </select>
        </div>
        <div>
          <label class="label">Desde</label>
          <input v-model="filters.from" @change="fetchRequests" type="date" class="field" />
        </div>
        <div>
          <label class="label">Hasta</label>
          <input v-model="filters.to" @change="fetchRequests" type="date" class="field" />
        </div>
        <div class="flex items-end">
          <button type="button" @click="clearFilters" class="w-full secondary-button justify-center">
            Limpiar filtros
          </button>
        </div>
      </div>
    </section>

    <section class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
      <article v-for="item in summaryCards" :key="item.status" class="summary-card">
        <div class="text-2xl font-bold" :class="item.color">{{ item.count }}</div>
        <div class="text-xs uppercase tracking-wide text-gray-500">{{ item.label }}</div>
      </article>
    </section>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>
    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
      <button type="button" @click="fetchRequests" class="ml-2 underline font-semibold">Reintentar</button>
    </div>
    <div v-else-if="requests.length === 0" class="empty-card">
      <div class="mx-auto w-14 h-14 rounded-full bg-green-50 text-green-700 flex items-center justify-center mb-4">
        <i class="fa-regular fa-circle-check text-2xl"></i>
      </div>
      <h2 class="text-lg font-semibold text-gray-900">No hay solicitudes pendientes de revision</h2>
      <p class="mt-1 text-sm text-gray-500">Podes cambiar los filtros o cargar una solicitud para un docente.</p>
      <button type="button" @click="showAll" class="mt-5 secondary-button">
        Ver todas
      </button>
    </div>

    <div v-else class="space-y-4">
      <article
        v-for="request in requests"
        :key="requestId(request)"
        class="bg-white border border-gray-200 rounded-xl shadow-sm p-4 hover:border-indigo-200 transition-colors"
      >
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <h2 class="text-lg font-bold text-gray-900">{{ requestTeacherName(request) }}</h2>
              <span v-if="isNew(request)" class="chip bg-indigo-50 text-indigo-700 border-indigo-100">Nueva</span>
              <span v-if="hasPendingDocuments(request)" class="chip bg-orange-50 text-orange-700 border-orange-100">Doc pendiente</span>
            </div>
            <p v-if="requestTeacherMeta(request)" class="text-xs text-gray-500">{{ requestTeacherMeta(request) }}</p>
            <div class="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-sm">
              <div>
                <div class="label mb-0">Articulo</div>
                <div class="font-semibold text-gray-900">{{ requestTitle(request) }}</div>
              </div>
              <div>
                <div class="label mb-0">Periodo</div>
                <div class="font-semibold text-gray-900">{{ dateRange(request) }} <span class="font-normal text-gray-500">· {{ requestedDays(request) }}</span></div>
              </div>
              <div>
                <div class="label mb-0">Estado</div>
                <span class="inline-flex px-2 py-1 text-xs font-semibold rounded-full border" :class="statusClass(request.status)">
                  {{ statusLabel(request.status) }}
                </span>
              </div>
              <div v-if="request.article_requires_final_document">
                <div class="label mb-0">Presento Constancia?</div>
                <div class="flex items-center gap-2">
                  <div class="font-semibold text-gray-900">{{ finalDocumentLabel(request) }}</div>
                  <button
                    type="button"
                    class="text-gray-400 hover:text-indigo-700"
                    title="Editar fecha de constancia"
                    @click="openFinalDocumentModal(request)"
                  >
                    <i class="fa-solid fa-pencil"></i>
                  </button>
                  <button
                    v-if="request.final_document_submitted_at"
                    type="button"
                    class="text-gray-400 hover:text-red-600"
                    title="Quitar fecha de constancia"
                    @click="openRemoveFinalDocumentModal(request)"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
            <p class="mt-3 text-xs text-gray-500">{{ secondaryDate(request) }}</p>
          </div>
          <div class="flex lg:justify-end">
            <button type="button" @click="reviewRequest(request)" class="primary-button">
              Revisar <i class="fa-solid fa-arrow-right ml-2"></i>
            </button>
          </div>
        </div>
      </article>

      <p class="text-center text-xs text-gray-500">Mostrando {{ requests.length }} solicitudes</p>
    </div>

    <div
      v-if="finalDocumentModal.open"
      class="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4"
    >
      <div class="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <h2 class="text-lg font-bold text-gray-900">Fecha de constancia</h2>
            <p class="mt-1 text-sm text-gray-600">Indicá la fecha en que se presento la constancia.</p>
          </div>
          <button type="button" class="text-gray-400 hover:text-gray-700" @click="closeFinalDocumentModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="p-5">
          <label class="label">Fecha</label>
          <input v-model="finalDocumentModal.date" type="date" class="field" />
          <p v-if="finalDocumentModal.error" class="mt-3 text-sm text-red-600">{{ finalDocumentModal.error }}</p>
        </div>
        <div class="px-5 py-4 bg-gray-50 flex justify-end gap-2">
          <button type="button" class="secondary-button" :disabled="finalDocumentModal.saving" @click="closeFinalDocumentModal">
            Cancelar
          </button>
          <button
            type="button"
            class="primary-button disabled:opacity-50"
            :disabled="finalDocumentModal.saving || !finalDocumentModal.date"
            @click="saveFinalDocument"
          >
            {{ finalDocumentModal.saving ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="removeFinalDocumentModal.open"
      class="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4"
    >
      <div class="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <h2 class="text-lg font-bold text-gray-900">Quitar constancia</h2>
            <p class="mt-1 text-sm text-gray-600">
              ¿Confirmas que queres eliminar la fecha de presentacion de la constancia?
            </p>
          </div>
          <button type="button" class="text-gray-400 hover:text-gray-700" @click="closeRemoveFinalDocumentModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div v-if="removeFinalDocumentModal.error" class="px-5 pt-4">
          <p class="text-sm text-red-600">{{ removeFinalDocumentModal.error }}</p>
        </div>
        <div class="px-5 py-4 bg-gray-50 flex justify-end gap-2">
          <button
            type="button"
            class="secondary-button"
            :disabled="removeFinalDocumentModal.saving"
            @click="closeRemoveFinalDocumentModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            :disabled="removeFinalDocumentModal.saving"
            @click="confirmRemoveFinalDocument"
          >
            {{ removeFinalDocumentModal.saving ? 'Eliminando...' : 'Aceptar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import { leaveArticlesApi, leaveRequestsApi } from '@/service/leave.service';
import { teachersApi } from '@/service/user.service';
import {
  LEAVE_STATUSES,
  articleTitle,
  formatDate,
  formatDateTime,
  requestDocuments,
  requestTeacherMeta,
  requestTeacherName,
  requestTitle,
  statusClass,
  statusLabel,
  teacherFullName,
  unwrapList,
} from '@/utils/leave';

export default {
  name: 'LeaveInboxView',
  components: { Loading },
  data() {
    return {
      loading: false,
      error: '',
      requests: [],
      articles: [],
      teachers: [],
      filters: {
        status: this.$route.query.status || 'pending',
        user_id: this.$route.query.user_id || '',
        leave_article_id: this.$route.query.leave_article_id || '',
        from: this.$route.query.from || '',
        to: this.$route.query.to || '',
      },
      statuses: LEAVE_STATUSES,
      finalDocumentModal: {
        open: false,
        saving: false,
        error: '',
        requestId: null,
        date: '',
      },
      removeFinalDocumentModal: {
        open: false,
        saving: false,
        error: '',
        requestId: null,
      },
    };
  },
  computed: {
    summaryCards() {
      const count = (status) => this.requests.filter((request) => request.status === status).length;
      return [
        { status: 'pending', label: 'Pendientes', count: count('pending'), color: 'text-yellow-600' },
        { status: 'observed', label: 'Observadas', count: count('observed'), color: 'text-orange-600' },
        { status: 'suspended', label: 'Suspendidas', count: count('suspended'), color: 'text-blue-600' },
        { status: 'approved', label: 'Aprobadas', count: count('approved'), color: 'text-green-600' },
      ];
    },
  },
  async created() {
    await Promise.all([this.fetchArticles(), this.fetchTeachers()]);
    await this.fetchRequests();
  },
  methods: {
    articleTitle,
    teacherFullName,
    requestTeacherName,
    requestTeacherMeta,
    requestTitle,
    statusClass,
    statusLabel,
    tabClass(path) {
      return this.$route.path === path ? 'module-tab-active' : '';
    },
    async fetchArticles() {
      try {
        const response = await leaveArticlesApi.list({ active: true });
        this.articles = unwrapList(response);
      } catch {
        this.articles = [];
      }
    },
    async fetchTeachers() {
      try {
        const response = await teachersApi.list({ status: 'activo' });
        this.teachers = unwrapList(response);
      } catch {
        this.teachers = [];
      }
    },
    async fetchRequests() {
      this.loading = true;
      this.error = '';
      try {
        this.syncQuery();
        const response = await leaveRequestsApi.list(this.filters);
        this.requests = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar las solicitudes.';
      } finally {
        this.loading = false;
      }
    },
    syncQuery() {
      this.$router.replace({ path: this.$route.path, query: { ...this.filters } }).catch(() => {});
    },
    clearFilters() {
      this.filters = { status: 'pending', user_id: '', leave_article_id: '', from: '', to: '' };
      this.fetchRequests();
    },
    showAll() {
      this.filters.status = '';
      this.fetchRequests();
    },
    requestId(request) {
      return request.id || request.request_id;
    },
    reviewRequest(request) {
      this.$router.push(`/leave/requests/${this.requestId(request)}/review`);
    },
    dateRange(request) {
      const start = formatDate(request.start_date);
      const end = formatDate(request.end_date);
      return start === end ? start : `${start} -> ${end}`;
    },
    requestedDays(request) {
      const days = request.requested_days ?? request.days ?? request.total_days;
      if (!days) return 'Sin duracion';
      return `${days} ${Number(days) === 1 ? 'dia' : 'dias'}`;
    },
    secondaryDate(request) {
      if (request.created_at) return `Enviada/creada: ${formatDateTime(request.created_at)}`;
      return 'Sin fecha de creacion';
    },
    finalDocumentLabel(request) {
      if (!request.final_document_submitted_at) return 'No';
      return `Si · ${formatDate(request.final_document_submitted_at)}`;
    },
    toDateInputValue(value) {
      if (!value) return '';
      const match = `${value}`.match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : '';
    },
    openFinalDocumentModal(request) {
      this.finalDocumentModal = {
        open: true,
        saving: false,
        error: '',
        requestId: this.requestId(request),
        date: this.toDateInputValue(request.final_document_submitted_at) || this.toDateInputValue(new Date().toISOString()),
      };
    },
    closeFinalDocumentModal() {
      if (this.finalDocumentModal.saving) return;
      this.finalDocumentModal = {
        open: false,
        saving: false,
        error: '',
        requestId: null,
        date: '',
      };
    },
    async saveFinalDocument() {
      if (!this.finalDocumentModal.requestId || !this.finalDocumentModal.date) return;
      this.finalDocumentModal.saving = true;
      this.finalDocumentModal.error = '';
      try {
        await leaveRequestsApi.submitFinalDocument(this.finalDocumentModal.requestId, {
          submitted_at: this.finalDocumentModal.date,
        });
        const request = this.requests.find((item) => this.requestId(item) === this.finalDocumentModal.requestId);
        if (request) {
          request.final_document_submitted_at = this.finalDocumentModal.date;
        }
        this.finalDocumentModal.saving = false;
        this.closeFinalDocumentModal();
      } catch (error) {
        this.finalDocumentModal.error = error?.response?.data?.message || 'No se pudo guardar la fecha de constancia.';
        this.finalDocumentModal.saving = false;
      }
    },
    openRemoveFinalDocumentModal(request) {
      this.removeFinalDocumentModal = {
        open: true,
        saving: false,
        error: '',
        requestId: this.requestId(request),
      };
    },
    closeRemoveFinalDocumentModal() {
      if (this.removeFinalDocumentModal.saving) return;
      this.removeFinalDocumentModal = {
        open: false,
        saving: false,
        error: '',
        requestId: null,
      };
    },
    async confirmRemoveFinalDocument() {
      if (!this.removeFinalDocumentModal.requestId) return;
      this.removeFinalDocumentModal.saving = true;
      this.removeFinalDocumentModal.error = '';
      try {
        await leaveRequestsApi.deleteFinalDocument(this.removeFinalDocumentModal.requestId);
        const request = this.requests.find((item) => this.requestId(item) === this.removeFinalDocumentModal.requestId);
        if (request) {
          request.final_document_submitted_at = null;
        }
        this.removeFinalDocumentModal.saving = false;
        this.closeRemoveFinalDocumentModal();
      } catch (error) {
        this.removeFinalDocumentModal.error =
          error?.response?.data?.message || 'No se pudo quitar la fecha de constancia.';
        this.removeFinalDocumentModal.saving = false;
      }
    },
    hasPendingDocuments(request) {
      return requestDocuments(request).some((document) => !document.status || document.status === 'pending');
    },
    isNew(request) {
      if (!request.created_at || request.status !== 'pending') return false;
      const created = new Date(request.created_at).getTime();
      return Date.now() - created < 86400000;
    },
  },
};
</script>

<style scoped>
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.label {
  @apply block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1;
}
.filter-card {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-5;
}
.summary-card {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-4;
}
.empty-card {
  @apply bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center;
}
.primary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800 shadow-sm;
}
.secondary-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50;
}
.module-tabs {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-1 mb-5 inline-flex flex-wrap gap-1;
}
.module-tab {
  @apply px-4 py-2 text-sm font-semibold text-gray-600 rounded-md hover:bg-gray-50;
}
.module-tab-active {
  @apply bg-indigo-900 text-white hover:bg-indigo-900;
}
.chip {
  @apply inline-flex px-2 py-1 text-xs font-semibold rounded-full border;
}

/*
 * @apply bakes fixed light-mode colors into these scoped classes, so the
 * global dark-mode retrofit in style.css (which only overrides classes
 * literally present in the DOM) can never reach them. Same story for a few
 * badge/text color combos below that the retrofit doesn't cover for anyone.
 */
.dark .field {
  @apply bg-gray-700 border-gray-600 text-gray-100;
}
.dark .label {
  @apply text-gray-400;
}
.dark .filter-card,
.dark .summary-card {
  @apply bg-gray-800 border-gray-700;
}
.dark .empty-card {
  @apply bg-gray-800 border-gray-600;
}
.dark .secondary-button {
  @apply bg-gray-800 border-gray-600 text-gray-200;
}
.dark .secondary-button:hover {
  @apply bg-gray-700;
}
.dark .module-tabs {
  @apply bg-gray-800 border-gray-700;
}
.dark .module-tab {
  @apply text-gray-300;
}
.dark .module-tab:hover {
  @apply bg-gray-700;
}

/* Status/summary badge colors not covered by the global retrofit. */
.dark .bg-yellow-100 {
  background-color: rgba(234, 179, 8, 0.2);
}
.dark .bg-orange-100 {
  background-color: rgba(249, 115, 22, 0.2);
}
.dark .bg-blue-100 {
  background-color: rgba(59, 130, 246, 0.2);
}
.dark .text-yellow-800 {
  color: theme('colors.yellow.300');
}
.dark .text-orange-800 {
  color: theme('colors.orange.300');
}
.dark .text-blue-800 {
  color: theme('colors.blue.300');
}
.dark .text-yellow-600 {
  color: theme('colors.yellow.400');
}
.dark .text-orange-600 {
  color: theme('colors.orange.400');
}
.dark .text-blue-600 {
  color: theme('colors.blue.400');
}
.dark .text-green-600 {
  color: theme('colors.green.400');
}
.dark .text-red-600 {
  color: theme('colors.red.400');
}
.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.35);
}
.dark .border-green-200 {
  border-color: rgba(34, 197, 94, 0.35);
}
.dark .border-yellow-200 {
  border-color: rgba(234, 179, 8, 0.35);
}
.dark .border-orange-100,
.dark .border-orange-200 {
  border-color: rgba(249, 115, 22, 0.35);
}
.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.35);
}
.dark .border-indigo-100 {
  border-color: rgba(99, 102, 241, 0.35);
}
.dark .hover\:border-indigo-200:hover {
  border-color: rgba(99, 102, 241, 0.5);
}
.dark .hover\:text-indigo-700:hover {
  color: theme('colors.indigo.300');
}
.dark .hover\:text-red-600:hover {
  color: theme('colors.red.400');
}
</style>
